import React, { useMemo, useState } from "react";
import RailGroup from "./RailGroup";
import { behindView } from "./railModel.mjs";
import { bothZones } from "./zones.mjs";
import { shortDate, firstName } from "./format.mjs";
import { latestFormSubmission } from "../formAnswers.mjs";

/*
 * BEHIND THE SCENES, collapsed at the bottom (the matrix's last row; never drawn
 * open, so built in the drawn card grammar from the brief's "Details that must
 * survive", Gabriel's ruling of 2026-10-01).
 *
 * ONLY WHAT CAN ACT ON THIS COMPANY NOW. Which automations apply is the server's
 * answer (GET /companies/:id/automations, PR #155: applies, why_not, runs_when,
 * last_run), never worked out here: the first version of that check, written on
 * the client, said "applies" for a call no reminder sequence covered. Each row
 * that applies carries
 *   - its switch in a NEUTRAL outline (On, Off for this company, Draft, Dry run:
 *     the brief retired the green "On" pill);
 *   - what makes it act at all ("Only answers the website form, filled in outside
 *     your hours", never "if they write": reality.txt [20]);
 *   - what it last did here ("Auto-replied Sep 12, scored 52");
 *   - Turn off for this company, asking an optional why, or Turn back on. Off is
 *     a standing decision with a reason and a date, and it cancels what is queued
 *     (the route's job), which is why it is a row and not a checkbox;
 *   - for pre-call reminders, each planned call still ahead with its reminders
 *     and Skip this call / Put the reminders back.
 * What does not apply is said once, in words, so a client's record explains why
 * the group is quiet ("The after-hours inbound auto-reply doesn't answer
 * pluto.security, a client's domain, so it's left out.").
 *
 * The booking link (prospects and parked companies) shows the greeting they will
 * see, Copy, Preview as them and the warnings the server raises; the audited flag
 * (prospects past their first email) is the hand flag of migration 043. Both read
 * the shell's payloads (record.booking, the company) instead of fetching their own:
 * the old booking box also asked GET /booking/settings, which calls Gmail live.
 *
 * Every confirming button here is black and every other one white: the yellow
 * on this page belongs to Next.
 *
 * Props:
 *   record   the shell's bundle: automations ({ automations, plans }), company,
 *            contact, booking (GET /booking/contact/:id), view.behind
 *            ({ booking, audit }), zones, timeline.rows (the form)
 *   ops      ops.call(path, options), ops.reload, ops.patchCompany(body)
 *   state    "open" | "summary"
 *   sheet    boolean, phone
 */

function AutomationRow({ row, companyId, ops }) {
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await ops.reload();
      setAsking(false);
      setNote("");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const path = `/companies/${companyId}/automations/${row.id}/off`;
  const turnOff = () => run(() => ops.call(path, { method: "PUT", body: JSON.stringify({ note: note.trim() || null }) }));
  const turnOn = () => run(() => ops.call(path, { method: "DELETE" }));

  return (
    <div className="rec-auto">
      <div className="rec-auto__head">
        <span className="rec-strong">{row.name}</span>
        <span className={`rec-tag rec-auto__state rec-auto__state--${row.state.key}`}>{row.state.label}</span>
      </div>
      {row.offLine && <div className="rec-auto__line">{row.offLine}</div>}
      {row.runsWhen && <div className="rec-auto__line">{row.runsWhen}</div>}
      <div className="rec-auto__line">{row.lastRun || "Hasn’t acted on this company yet."}</div>
      {asking ? (
        <div className="rec-stack">
          <input
            className="rec-input"
            autoFocus
            aria-label="Why? (optional)"
            placeholder="Why? (optional)"
            value={note}
            disabled={busy}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && turnOff()}
          />
          <div className="rec-actionsrow">
            <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={busy} onClick={turnOff}>{busy ? "Turning off…" : "Turn off"}</button>
            <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" disabled={busy} onClick={() => setAsking(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="rec-actionsrow">
          <button type="button" className="rec-btn rec-btn--secondary rec-btn--sm" disabled={busy} onClick={row.off ? turnOn : () => setAsking(true)}>
            {busy ? "…" : row.off ? "Turn back on" : "Turn off for this company"}
          </button>
        </div>
      )}
      {error && <span className="rec-err">{error}</span>}
    </div>
  );
}

function PlanRow({ item, record, ops }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const p = item.plan;
  const person = ((record.company && record.company.contacts) || []).find((c) => String(c.id) === String(p.contact_id)) || null;
  // Their zone, the person the reminders go to; Gabriel's alone when unknown.
  const zones = { ...record.zones, theirs: (person && person.local_zone) || null };
  const when = (ts) => {
    const b = bothZones(ts, zones, { name: person ? firstName(person) : null });
    return `${shortDate(ts, { now: record.now })} · ${b ? b.text : ""}`;
  };
  const skipPath = `/automations/${p.automation_id}/calls/${encodeURIComponent(p.event_id)}/skip`;
  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await ops.reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const skip = () =>
    run(() =>
      ops.call(skipPath, {
        method: "POST",
        body: JSON.stringify({ company_id: p.company_id, contact_id: p.contact_id, call_at: p.call_at, call_title: p.call_title }),
      })
    );
  const unskip = () => run(() => ops.call(`${skipPath}?company_id=${encodeURIComponent(p.company_id)}`, { method: "DELETE" }));

  return (
    <div className="rec-plan">
      <div className="rec-strong">{item.title}</div>
      <div className="rec-auto__line">{when(p.call_at)}</div>
      {item.note ? (
        <div className="rec-auto__line">{item.note}</div>
      ) : item.reminders.length ? (
        <ul className="rec-plan__reminders">
          {item.reminders.map((r) => (
            <li key={r.key}>
              {r.at ? when(r.at) : "time not set"} <span className="rec-sub">· {r.outcome}{r.blocked ? `, ${r.blocked}` : ""}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rec-auto__line">Nothing queued yet.</div>
      )}
      {item.canSkip && (
        <div className="rec-actionsrow">
          <button type="button" className="rec-btn rec-btn--secondary rec-btn--sm" disabled={busy} onClick={item.skipped ? unskip : skip}>
            {busy ? "…" : item.skipped ? "Put the reminders back" : "Skip this call"}
          </button>
        </div>
      )}
      {error && <span className="rec-err">{error}</span>}
    </div>
  );
}

function BookingLink({ card, ops }) {
  const [copied, setCopied] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(card.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("The browser blocked the clipboard. The link is in the preview.");
    }
  };
  const preview = async () => {
    // The tab is opened inside the click, so a popup blocker lets it through, and
    // pointed at the page once the preview token comes back (it books nothing).
    const tab = window.open("about:blank", "_blank");
    setOpening(true);
    setError(null);
    try {
      const { token } = await ops.call("/booking/preview-token", { method: "POST" });
      const url = `/book?${new URLSearchParams({ c: card.invite, p: token })}`;
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (e) {
      if (tab) tab.close();
      setError(e.message);
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="rec-bts__section">
      <span className="rec-label">Personal booking link</span>
      <div className="rec-body">
        They’ll see: <b className="rec-strong">“{card.greeting}”</b>
        {card.prefill && card.prefill.email ? <span className="rec-sub"> · name and email already filled in</span> : null}
      </div>
      {(card.warnings || []).map((w) => (
        <div key={w} className="rec-bts__warn">{w}</div>
      ))}
      {!card.open && card.url && <div className="rec-bts__warn">The booking page is switched off, so this link shows “paused” until you turn it on.</div>}
      <div className="rec-actionsrow">
        <button type="button" className="rec-btn rec-btn--secondary rec-btn--sm" disabled={!card.url} onClick={copy}>{copied ? "Copied" : "Copy"}</button>
        {card.invite && (
          <button type="button" className="rec-link" disabled={opening} onClick={preview}>{opening ? "Opening…" : "Preview as them ↗"}</button>
        )}
      </div>
      {error && <span className="rec-err">{error}</span>}
    </div>
  );
}

function Audited({ company, ops }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const audited = !!company.audited_at;
  const notes = (company.audit_notes || "").trim();

  const open = () => {
    setForm({ audited, notes: company.audit_notes || "" });
    setError(null);
    setEditing(true);
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      // The date stays when it stays ticked: it is when the audit was done, not
      // when the notes were last touched (AuditNotes' rule).
      await ops.patchCompany({
        audited_at: form.audited ? company.audited_at || new Date().toISOString() : null,
        audit_notes: form.notes.trim() || null,
      });
      setEditing(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rec-bts__section">
      <div className="rec-auto__head">
        <span className="rec-label">LinkedIn audit</span>
        <span className="rec-tag">{audited ? `audited ${shortDate(company.audited_at)}` : "not audited"}</span>
      </div>
      {editing && form ? (
        <div className="rec-stack">
          <label className="rec-bts__check">
            <input type="checkbox" checked={form.audited} disabled={busy} onChange={(e) => setForm({ ...form, audited: e.target.checked })} />
            <span>We audited their LinkedIn Ads account</span>
          </label>
          <textarea
            className="rec-textarea"
            rows={4}
            aria-label="What the audit found"
            placeholder="What the audit found: structure, targeting, the pitch it sets up"
            value={form.notes}
            disabled={busy}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
          <div className="rec-actionsrow">
            <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
            <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
            {error && <span className="rec-err">{error}</span>}
          </div>
        </div>
      ) : (
        <>
          <div className="rec-body">{notes || (audited ? "No notes yet." : "Gave us viewer access? Mark it once the audit is done.")}</div>
          <div className="rec-actionsrow">
            <button type="button" className="rec-btn rec-btn--secondary rec-btn--sm" onClick={open}>{audited || notes ? "Edit" : "Mark audited"}</button>
          </div>
        </>
      )}
    </div>
  );
}

export default function BehindScenes({ record, ops, state = "summary", sheet = false }) {
  const co = record.company;
  const form = useMemo(() => latestFormSubmission(record.timeline.rows), [record.timeline.rows]);
  const view = behindView({ automations: record.automations, show: record.view.behind || {}, company: co, booking: record.booking, form, now: record.now });
  const quiet = !view.acting.length && !view.booking && !view.audit;
  const prospectOnly = co && co.status !== "prospect" && co.status !== "recycled";

  return (
    <RailGroup id="behind" title="Behind the scenes" summary={view.summary} defaultOpen={state === "open"} sheet={sheet}>
      {view.acting.map((a) => (
        <AutomationRow key={a.id} row={a} companyId={co && co.id} ops={ops} />
      ))}
      {view.plans.length > 0 && (
        <div className="rec-bts__section">
          <span className="rec-label">Pre-call reminders, call by call</span>
          {view.plans.map((p) => (
            <PlanRow key={p.id} item={p} record={record} ops={ops} />
          ))}
        </div>
      )}
      {view.booking && <BookingLink card={view.booking} ops={ops} />}
      {view.audit && <Audited company={co} ops={ops} />}
      {(quiet || view.leftOut.length > 0) && (
        <div className="rec-bts__note">
          {[
            view.leftOut.length ? view.leftOut.join(" ") : quiet ? "No automation is set up to act here." : null,
            quiet && prospectOnly ? "The booking link and audit flag are for prospects." : null,
          ]
            .filter(Boolean)
            .join(" ")}
        </div>
      )}
    </RailGroup>
  );
}
